<script setup lang="ts">
/**
 * HLS Video Player using Shaka Player.
 * Always uses Shaka for HLS playback.
 */
import type { ShakaPlayer } from '~/composables/useHlsPlayer/types';

import { useEventListener, useFullscreen, whenever } from '@vueuse/core';
import {
	createPlayer,
	destroyPlayer
} from '~/composables/useSharedVideoPlayer';
import { getProxiedVideoUrl } from '~/composables/useVideoUrls';

import IconMaterialSymbolsLightFullscreen from '~icons/material-symbols-light/fullscreen';
import IconMaterialSymbolsLightFullscreenExit from '~icons/material-symbols-light/fullscreen-exit';
import IconMaterialSymbolsLightVolumeOff from '~icons/material-symbols-light/volume-off';
import IconMaterialSymbolsLightVolumeUp from '~icons/material-symbols-light/volume-up';

interface HlsVideoPlayerProps {
	videoUrl: string;
	posterUrl?: string;
	autoplay?: boolean;
	muted?: boolean;
	loop?: boolean;
	controls?: boolean;
}

const props = withDefaults(defineProps<HlsVideoPlayerProps>(), {
	autoplay: true,
	muted: true,
	loop: false,
	controls: true
});

const containerRef = ref<HTMLDivElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);
const player = shallowRef<ShakaPlayer | null>(null);

const isReady = ref(false);
const hasError = ref(false);
const isVideoLoaded = ref(false);
const playing = ref(false);
const proxiedVideoUrl = computed(() => getProxiedVideoUrl(props.videoUrl));

// VueUse: Fullscreen control
const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(containerRef);

const isMuted = ref(props.muted);

watch(
	() => props.muted,
	val => {
		isMuted.value = val;
	}
);

function toggleMute() {
	isMuted.value = !isMuted.value;
}

async function togglePlay() {
	const video = videoRef.value;
	if (!video) return;

	if (video.paused) {
		try {
			await video.play();
		} catch {
			// Ignore play errors
		}
	} else {
		video.pause();
	}
}

async function initPlayer() {
	if (!import.meta.client || !videoRef.value) return;

	try {
		player.value = await createPlayer(proxiedVideoUrl.value, videoRef.value);

		player.value.addEventListener('error', (event: { detail: Error }) => {
			console.error('[HlsVideoPlayer] Shaka error:', event.detail);
			hasError.value = true;
		});

		videoRef.value.loop = props.loop;
		videoRef.value.muted = props.muted;

		if (props.autoplay) {
			try {
				await videoRef.value.play();
			} catch {
				// Autoplay blocked
			}
		}

		isReady.value = true;
	} catch (error) {
		console.error('[HlsVideoPlayer] Init error:', error);
		hasError.value = true;
	}
}

// Sync muted state with video element
watch(isMuted, newMuted => {
	if (videoRef.value) {
		videoRef.value.muted = newMuted;
	}
});

useEventListener(videoRef, 'canplay', () => {
	isReady.value = true;
	isVideoLoaded.value = true;
});

useEventListener(videoRef, 'loadedmetadata', () => {
	isVideoLoaded.value = true;
});

useEventListener(videoRef, 'loadeddata', () => {
	isVideoLoaded.value = true;
});

useEventListener(videoRef, 'error', () => {
	hasError.value = true;
});

useEventListener(videoRef, 'play', () => {
	playing.value = true;
});

useEventListener(videoRef, 'pause', () => {
	playing.value = false;
});

// Listen for reels modal events
let mutedBeforeModalOpen = false;

useEventListener(window, 'reels-modal-opened', () => {
	mutedBeforeModalOpen = isMuted.value;
	isMuted.value = true;
});

useEventListener(window, 'reels-modal-closed', () => {
	isMuted.value = mutedBeforeModalOpen;
});

// Watch for URL changes and reload
whenever(
	() => proxiedVideoUrl.value,
	async newUrl => {
		if (player.value && newUrl) {
			try {
				await player.value.load(newUrl);
			} catch (error) {
				console.error('[HlsVideoPlayer] Error loading new source:', error);
			}
		}
	}
);

onMounted(() => {
	initPlayer();
});

onBeforeUnmount(() => {
	destroyPlayer(player.value);
});
</script>

<template>
	<div
		ref="containerRef"
		class="hls-video-player relative size-full overflow-hidden bg-black"
		@click="togglePlay"
	>
		<!-- Poster Image (shown while video is loading) -->
		<div
			v-if="posterUrl && !isVideoLoaded && !hasError"
			class="absolute inset-0 z-0 size-full overflow-hidden"
		>
			<img
				:src="posterUrl"
				alt="Video thumbnail"
				class="size-full object-cover border-0 outline-none"
			/>
		</div>

		<!-- Video Element (Shaka manages the source) -->
		<video
			ref="videoRef"
			playsinline
			class="relative z-10 size-full cursor-pointer object-cover"
		/>

		<!-- Error State -->
		<div
			v-if="hasError"
			class="absolute inset-0 flex items-center justify-center bg-black/80"
		>
			<span class="text-sm text-white/70">Erro ao carregar vídeo</span>
		</div>

		<!-- Controls -->
		<div
			v-if="controls && isReady && !hasError"
			class="absolute bottom-4 right-4 z-10 flex items-center gap-2"
			@click.stop
		>
			<!-- Mute Button -->
			<button
				type="button"
				class="flex size-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-all hover:bg-black/80 active:scale-95"
				:aria-label="isMuted ? 'Ativar som' : 'Desativar som'"
				@click="toggleMute"
			>
				<IconMaterialSymbolsLightVolumeOff v-if="isMuted" class="size-5" />
				<IconMaterialSymbolsLightVolumeUp v-else class="size-5" />
			</button>

			<!-- Fullscreen Button -->
			<button
				type="button"
				class="flex size-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-all hover:bg-black/80 active:scale-95"
				:aria-label="isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'"
				@click="toggleFullscreen"
			>
				<IconMaterialSymbolsLightFullscreenExit
					v-if="isFullscreen"
					class="size-5"
				/>
				<IconMaterialSymbolsLightFullscreen v-else class="size-5" />
			</button>
		</div>
	</div>
</template>

<!-- eslint-disable-next-line custom-rules/no-style-tag -->
<style scoped>
.hls-video-player:fullscreen {
	background: black;
}

.hls-video-player:fullscreen video {
	object-fit: contain;
}
</style>
